import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { OrderQuery } from '@modules/order/state/order-query';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, map, Observable } from 'rxjs';

/**
 * Lock rules for the procedure/type selector (and the Pre-Treatment, Denture Copy, Emergence Profile
 * toggles) for Fixed Restorative and Denture/Removable only. Locked controls are disabled but keep
 * their values.
 */
@Injectable({
	providedIn: 'root'
})
export class ScanLayersLockService {
	private static readonly lockableProcedures: ProcedureEnum[] = [ProcedureEnum.FixedRestorative, ProcedureEnum.Denture_Removable];

	// Locked once a multilayer scan was added (host launch flag)
	readonly isScanLayersLocked$: Observable<boolean> = combineLatest([
		this.shellQuery.hasMultipleScanLayers$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(
			([hasMultipleScanLayers, procedureMap]) =>
				hasMultipleScanLayers && this.isLockableProcedure(procedureMap?.ProcedureId as ProcedureEnum | undefined)
		)
	);

	// Locked while a duplicated case keeps its cloned procedure; releasing once the user switches it.
	readonly isDuplicatedCaseLocked$: Observable<boolean> = combineLatest([
		this.shellQuery.isCloningMode$,
		this.shellQuery.isProcedureChanged$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(
			([isCloningMode, isProcedureChanged, procedureMap]) =>
				isCloningMode && !isProcedureChanged && this.isLockableProcedure(procedureMap?.ProcedureId as ProcedureEnum | undefined)
		)
	);

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery
	) {}

	private isLockableProcedure(procedureId: ProcedureEnum | undefined): boolean {
		return procedureId !== undefined && ScanLayersLockService.lockableProcedures.includes(procedureId);
	}
}
